import React, { useState } from "react";
import { CoObjectItem } from "../../interfaces/CoObject";
import { ImageFit, Link, Stack } from "@fluentui/react";
import { Checkbox, CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { IconButton, IButtonStyles } from "@fluentui/react/lib/Button";
import { CoObjectTypeEnum } from "../../../api/models";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";

export interface ObjectListItemButton {
  key: string;
  icon: string;
  ariaLabel?: string;
  onClick: (item: CoObjectItem) => void;
  buttonStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
}

const getObjectDisplayName = (objectItem: CoObjectItem) => {
  if (objectItem.info.objectType === CoObjectTypeEnum.DOCUMENT_PART_INCLUDE) {
    return objectItem.info.objectName.toString().replace(/\.docx$/i, "");
  }
  return objectItem.info.objectName;
};

const getFluentButtonStyles = (buttonStyle?: React.CSSProperties): IButtonStyles | undefined => {
  if (!buttonStyle) return undefined;

  const stateStyle = {
    backgroundColor: typeof buttonStyle.backgroundColor === "string" ? buttonStyle.backgroundColor : undefined,
    borderRadius:
      typeof buttonStyle.borderRadius === "string" || typeof buttonStyle.borderRadius === "number"
        ? buttonStyle.borderRadius
        : undefined,
    height:
      typeof buttonStyle.height === "string" || typeof buttonStyle.height === "number" ? buttonStyle.height : undefined,
    minWidth:
      typeof buttonStyle.minWidth === "string" || typeof buttonStyle.minWidth === "number"
        ? buttonStyle.minWidth
        : undefined,
    padding:
      typeof buttonStyle.padding === "string" || typeof buttonStyle.padding === "number"
        ? buttonStyle.padding
        : undefined,
    width:
      typeof buttonStyle.width === "string" || typeof buttonStyle.width === "number" ? buttonStyle.width : undefined,
  };

  return {
    root: stateStyle,
    rootHovered: stateStyle,
    rootPressed: stateStyle,
    rootFocused: stateStyle,
  };
};

const ItemActionButtons: React.FC<{
  buttons: ObjectListItemButton[];
  objectItem: CoObjectItem;
  alignRight: boolean;
}> = ({ buttons, objectItem, alignRight }) => (
  <Stack horizontal style={alignRight ? { marginLeft: "auto" } : undefined}>
    {buttons.map((button) => (
      <IconButton
        key={button.key}
        ariaLabel={button.ariaLabel}
        iconProps={{
          imageProps: {
            src: button.icon,
            imageFit: ImageFit.contain,
            style: button.iconStyle,
          },
          styles: { root: { fontWeight: "bold" } },
        }}
        style={button.buttonStyle}
        styles={getFluentButtonStyles(button.buttonStyle)}
        onClick={(e) => {
          e.stopPropagation();
          button.onClick(objectItem);
        }}
      />
    ))}
  </Stack>
);

const ItemStatusIcons: React.FC<{
  objectItem: CoObjectItem;
  showStatusIcons: boolean;
  hasComponentIcon: boolean;
}> = ({ objectItem, showStatusIcons, hasComponentIcon }) => (
  <>
    {showStatusIcons && objectItem.isLocked ? (
      <img
        src="/assets/lock.svg"
        alt="Lock Icon"
        style={{
          objectFit: "contain",
          paddingLeft: "5px",
          backgroundRepeat: "no",
          backgroundPosition: "right center",
        }}
      />
    ) : null}
    {hasComponentIcon ? (
      <span
        className={`${objectItem.info.isComponent ? "component_title" : "instance_component_title"} right_icon_class`}
      />
    ) : null}
  </>
);

const ItemLabel: React.FC<{
  objectItem: CoObjectItem;
  showObjectIcon: boolean;
  showStatusIcons: boolean;
  showComponentIcons: boolean;
  highlighted: boolean;
  clickable: boolean;
  grow: boolean;
  onLinkClick: (event: { shiftKey: boolean }) => void;
}> = ({
  objectItem,
  showObjectIcon,
  showStatusIcons,
  showComponentIcons,
  highlighted,
  clickable,
  grow,
  onLinkClick,
}) => {
  const hasComponentIcon =
    showComponentIcons && (objectItem.info.isComponent || objectItem.info.componentReference !== undefined);

  return (
    <Link
      style={{
        color: "var(--color-black)",
        cursor: clickable ? "pointer" : "default",
        flex: grow ? 1 : "0 1 auto",
        minWidth: 0,
        overflow: "hidden",
      }}
      key={`link-${objectItem.id}`}
      id={generateIdentifier(objectItem.info.objectName, "objectlink")}
      onClick={onLinkClick}
    >
      <div
        style={{
          backgroundColor: highlighted ? "rgb(237, 235, 233)" : null,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {showObjectIcon ? (
          <span className={`${objectItem.info.objectType} icon_class`} style={{ lineHeight: 1 }} />
        ) : null}
        <span style={{ lineHeight: 1 }}>{getObjectDisplayName(objectItem)}</span>
        <ItemStatusIcons
          objectItem={objectItem}
          showStatusIcons={showStatusIcons}
          hasComponentIcon={hasComponentIcon}
        />
      </div>
    </Link>
  );
};

export const ObjectListItemRow = ({
  objectItem,
  onClick,
  onToggleSelect,
  showObjectIcon = true,
  showStatusIcons = true,
  showComponentIcons = true,
  selected = false,
  selectable = true,
  highlighted = false,
  buttons = [],
  hoverButtons,
  buttonsAlignment = "right",
}: {
  objectItem: CoObjectItem;
  onClick?: (item: CoObjectItem, shiftKey?: boolean) => void;
  onToggleSelect?: (item: CoObjectItem, selected?: boolean, shiftKey?: boolean) => void;
  showObjectIcon?: boolean;
  showStatusIcons?: boolean;
  showComponentIcons?: boolean;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  buttons?: ObjectListItemButton[];
  hoverButtons?: ObjectListItemButton[];
  buttonsAlignment?: "left" | "right";
}) => {
  const [isPointerInside, setIsPointerInside] = useState(false);

  const hasHoverButtons = hoverButtons !== undefined && hoverButtons.length > 0;
  const showHoverButtons = isPointerInside;
  const alignRight = buttonsAlignment !== "left";
  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    if (!selectable) return;
    onToggleSelect?.(objectItem, event.value, event.nativeEvent.shiftKey);
  };

  const handleLinkClick = (event: { shiftKey: boolean }) => {
    onClick?.(objectItem, event.shiftKey);
  };

  const hoverHandlers = hasHoverButtons
    ? {
        onMouseEnter: () => setIsPointerInside(true),
        onMouseLeave: () => setIsPointerInside(false),
      }
    : {};

  return (
    <div
      key={`div-${objectItem.id}`}
      style={onToggleSelect && !selectable ? { filter: "grayscale(100%)", opacity: 0.7 } : {}}
      {...hoverHandlers}
    >
      <Stack
        horizontal
        key={`item-${objectItem.id}`}
        style={{ alignItems: "center", minHeight: hasHoverButtons ? 28 : undefined, padding: "1px 0", width: "100%" }}
      >
        {onToggleSelect && (
          <div>
            <Checkbox
              checked={selected}
              disabled={!selectable}
              size="medium"
              rounded="small"
              style={{ marginRight: "5px", marginTop: "-2px" }}
              onChange={handleCheckboxChange}
              id={generateIdentifier(objectItem.info.objectName, "checkbox")}
            />
          </div>
        )}
        <ItemLabel
          objectItem={objectItem}
          showObjectIcon={showObjectIcon}
          showStatusIcons={showStatusIcons}
          showComponentIcons={showComponentIcons}
          highlighted={highlighted}
          clickable={onClick !== undefined}
          grow={alignRight}
          onLinkClick={handleLinkClick}
        />
        {buttons.length > 0 && <ItemActionButtons buttons={buttons} objectItem={objectItem} alignRight={alignRight} />}
        {hoverButtons && hoverButtons.length > 0 && showHoverButtons && (
          <ItemActionButtons buttons={hoverButtons} objectItem={objectItem} alignRight={alignRight} />
        )}
      </Stack>
    </div>
  );
};
